<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue"
import { ChevronLeft, ChevronRight, Settings } from "lucide-vue-next"
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "~/components/ui/drawer"
import { ScrollArea } from "~/components/ui/scroll-area"
import NotificationTargetPanel, { type NotificationPanelSection } from "~/components/voice-ui/Notifications/NotificationTargetPanel.vue"
import { useNotificationTargetDrawer } from "~/composables/useNotificationTargetDrawer"
import { useMobileDrawerDepthBackNav } from "~/composables/useMobileDrawerBackNav"
import { usePlatform } from "~/composables/usePlatform"
import { useSwipeBack } from "~/composables/useSwipeBack"
import InviteDialog from "~/components/voice-ui/GroupInfo/InviteDialog.vue"
import { useGroupsStore } from "~/stores/groups"
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar"
import { resolveMediaUrl } from "~/lib/mediaCache"

// Same list → page slide as the profile Drawer (MobileProfileDrawer.vue): the root pane lists
// «Заглушить …» and «Уведомления», each slides its choices in from the right; the back arrow, a
// swipe right or the Android back button slides back.
const { isMobile } = usePlatform()
const { isOpen, current } = useNotificationTargetDrawer()

const screen = ref<"root" | "page">("root")
// Kept until the slide back has finished, so the page doesn't blank out mid-slide; then cleared so
// the hidden pane doesn't stretch the sheet on the root screen.
const section = ref<NotificationPanelSection | null>(null)
const SLIDE_DURATION_MS = 300
let clearSectionTimer: ReturnType<typeof setTimeout> | null = null
watch(screen, (value) => {
  if (clearSectionTimer) clearTimeout(clearSectionTimer)
  clearSectionTimer = null
  if (value !== "root") return
  clearSectionTimer = setTimeout(() => {
    clearSectionTimer = null
    if (screen.value === "root") section.value = null
  }, SLIDE_DURATION_MS)
})
onBeforeUnmount(() => {
  if (clearSectionTimer) clearTimeout(clearSectionTimer)
})

// Every open starts on the root list.
watch(isOpen, (open) => {
  if (!open) return
  screen.value = "root"
  section.value = null
})

function openSection(value: NotificationPanelSection) {
  section.value = value
  screen.value = "page"
}

const MUTE_TITLES = { category: "Заглушить категорию", channel: "Заглушить канал" } as const
const pageTitle = computed(() => {
  if (section.value === "level") return "Уведомления"
  return current.value ? MUTE_TITLES[current.value.target] : ""
})

const navDepth = computed({
  get: () => (screen.value === "page" ? 1 : 0),
  set: (depth: number) => {
    if (depth < 1) screen.value = "root"
  },
})
useMobileDrawerDepthBackNav({ isOpen, depth: navDepth, enabled: isMobile })

// «Пригласить» on a channel: the same invite dialog as the group menu (GroupBanner.vue), opened
// once this Drawer is out of the way.
const groupsStore = useGroupsStore()
const group = computed(() => (current.value ? groupsStore.groupDetails[current.value.groupId] : undefined))
const groupAvatarUrl = computed(() => resolveMediaUrl(group.value?.avatar_url, "avatars"))
const groupInitials = computed(() => group.value?.name?.trim().substring(0, 2).toUpperCase() || "??")
const inviteDialogRef = ref<InstanceType<typeof InviteDialog> | null>(null)
const inviteGroupId = ref<string | null>(null)
const inviteGroupName = computed(() =>
  inviteGroupId.value ? groupsStore.groupDetails[inviteGroupId.value]?.name : undefined)
function openInvite() {
  if (!current.value) return
  inviteGroupId.value = current.value.groupId
  isOpen.value = false
  setTimeout(() => inviteDialogRef.value?.openForCreate(), SLIDE_DURATION_MS)
}

// «Настройки канала/категории» (managers): its own settings Drawer replaces this one.
function openEdit() {
  const onEdit = current.value?.onEdit
  if (!onEdit) return
  isOpen.value = false
  setTimeout(onEdit, SLIDE_DURATION_MS)
}

const stripRef = ref<HTMLElement | null>(null)
const { stripStyle, handlers: swipeBackHandlers } = useSwipeBack({
  stripRef,
  isOnSecondPane: () => screen.value === "page",
  onBack: () => { screen.value = "root" },
})
</script>

<template>
  <Drawer v-model:open="isOpen">
    <DrawerContent class="flex max-h-[94vh] flex-col gap-0 p-0">
      <div class="relative min-h-0 flex-1 overflow-hidden">
        <div
            ref="stripRef"
            class="flex h-full w-[200%] touch-pan-y transition-transform duration-300 ease-out [&_[data-slot=scroll-area-viewport]]:touch-pan-y"
            :style="stripStyle"
            v-on="swipeBackHandlers"
        >
          <div class="flex h-full w-1/2 min-h-0 flex-col overflow-y-auto overscroll-contain" data-testid="notification-drawer-root-pane">
            <!-- The group's avatar, then the channel/category name, left-aligned. -->
            <DrawerHeader class="flex-row items-center gap-3 text-left">
              <Avatar class="size-10 shrink-0">
                <AvatarImage v-if="group?.avatar_url" :src="groupAvatarUrl"/>
                <AvatarFallback class="text-xs">{{ groupInitials }}</AvatarFallback>
              </Avatar>
              <div class="grid min-w-0 flex-1">
                <DrawerTitle class="truncate">{{ current?.target === 'category' ? current?.title : `#${current?.title ?? ''}` }}</DrawerTitle>
                <DrawerDescription class="sr-only">Действия и уведомления</DrawerDescription>
              </div>
            </DrawerHeader>
            <div class="shrink-0 pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)]">
              <NotificationTargetPanel
                  v-if="current"
                  :group-id="current.groupId"
                  :target="current.target"
                  :target-id="current.targetId"
                  :category-id="current.categoryId"
                  @open="openSection"
                  @invite="openInvite"
                  @close="isOpen = false"
              />
              <div v-if="current?.onEdit" class="mx-4 mt-3 overflow-hidden rounded-2xl border bg-muted/40">
                <button
                    type="button"
                    class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
                    data-testid="notification-drawer-edit-row"
                    @click="openEdit"
                >
                  <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <Settings class="size-5"/>
                  </span>
                  <span class="min-w-0 flex-1 truncate text-[15px] font-medium">{{ current?.target === 'category' ? 'Настройки категории' : 'Настройки канала' }}</span>
                  <ChevronRight class="size-5 shrink-0 text-muted-foreground/70"/>
                </button>
              </div>
            </div>
          </div>

          <div class="flex h-full w-1/2 min-h-0 flex-col">
            <template v-if="current && section">
              <div class="relative flex shrink-0 items-center justify-center px-2 pb-2 pt-3">
                <button
                    type="button"
                    aria-label="Назад"
                    class="absolute left-3 flex size-10 items-center justify-center rounded-full bg-muted/70 text-muted-foreground transition-transform duration-150 active:scale-90"
                    @click="screen = 'root'"
                >
                  <ChevronLeft class="size-5"/>
                </button>
                <h2 class="text-lg font-semibold">{{ pageTitle }}</h2>
              </div>
              <ScrollArea class="min-h-0 flex-1">
                <div class="pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] pt-3">
                  <NotificationTargetPanel
                      :group-id="current.groupId"
                      :target="current.target"
                      :target-id="current.targetId"
                      :category-id="current.categoryId"
                      :section="section"
                      @open="openSection"
                      @back="screen = 'root'"
                  />
                </div>
              </ScrollArea>
            </template>
          </div>
        </div>
      </div>
    </DrawerContent>
  </Drawer>
  <InviteDialog v-if="inviteGroupId" ref="inviteDialogRef" :group-id="inviteGroupId" :group-name="inviteGroupName"/>
</template>
